import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { DateRange } from 'react-day-picker';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import {
  BuybackEventItem,
  CompanyIncorporationEventItem,
  ConversionEventItem,
  EventTypeBackend,
  EventUnion,
  ExerciseEventItem,
  FundraisingEventItem,
  GrantEventItem,
  SecondariesEventItem,
  ShareIssuanceEventItem,
  SortByDate,
} from '../../types/events.types';
import { Safe } from '../../types/safes.types';
import { useCompanyContextReady } from '../company/use-company-context-ready.hook';
import { useFormat } from '../use-format.hook';
import { dateFilterVariant } from './use-events';

type EventTransactionsResponse<
  TEvent extends
    | BuybackEventItem
    | CompanyIncorporationEventItem
    | ConversionEventItem
    | EventUnion
    | ExerciseEventItem
    | FundraisingEventItem
    | GrantEventItem
    | Safe
    | SecondariesEventItem
    | ShareIssuanceEventItem,
> = {
  items: TEvent[];
  totalPages: number;
};

export const useTransactions = <
  TEvent extends
    | BuybackEventItem
    | CompanyIncorporationEventItem
    | ConversionEventItem
    | EventUnion
    | ExerciseEventItem
    | FundraisingEventItem
    | GrantEventItem
    | Safe
    | SecondariesEventItem
    | ShareIssuanceEventItem = EventUnion,
>({
  companyId,
  eventId,
  eventType,
  currentPage = 0,
  take = 13,
  customDateFilter,
  dateFilter = SortByDate.ALL_TIME,
  transactionView = false,
  enabled = true,
}: {
  companyId: string;
  currentPage?: number;
  customDateFilter?: DateRange;
  dateFilter?: SortByDate;
  enabled?: boolean;
  eventId: string;
  eventType: EventTypeBackend;
  take?: number;
  transactionView?: boolean;
}) => {
  const queryClient = useQueryClient();
  const { format } = useFormat();
  const isCompanyContextReady = useCompanyContextReady(companyId);

  const dateRange = dateFilterVariant[dateFilter]({
    from: customDateFilter?.from,
    to: customDateFilter?.to,
  });

  const dateRangeTo = dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined;
  const dateRangeFrom = dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined;

  const skip = (currentPage - 1 <= 0 ? 0 : currentPage - 1) * take;

  const { data, fetchNextPage, hasNextPage, isLoading } = useInfiniteQuery<
    EventTransactionsResponse<TEvent>
  >({
    queryKey: [
      QueryKey.GET_EVENT_TRANSACTIONS,
      {
        eventId,
        eventType,
        take,
        companyId,
        currentPage,
        to: dateRangeTo,
        from: dateRangeFrom,
        transactionView,
      },
    ],
    queryFn: ({ pageParam = 0 }) =>
      ApiService.get(
        {
          endpoint: BackendRoute.COMPANIES,
          routePath: [companyId, 'events', eventId, eventType, 'transactions'],
        },
        {
          queryParams: {
            take,
            skip: currentPage ? skip : pageParam,
            'start-date': dateRangeFrom,
            'end-date': dateRangeTo,
            transactionView,
          },
        },
      ),

    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.totalPages > allPages.length ? allPages.length * take : undefined,
    enabled: !!companyId && !!eventId && enabled && isCompanyContextReady,
  });

  const items = useMemo(
    () =>
      data?.pages?.reduce<EventTransactionsResponse<TEvent>['items']>((acc, page) => {
        return [...acc, ...page.items];
      }, []),
    [data?.pages],
  );

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_EVENT_TRANSACTIONS] });
  }, [queryClient]);

  return {
    invalidateQuery,
    items: items || [],
    totalPages: data?.pages[0]?.totalPages || 0,
    hasNextPage,
    fetchNextPage,
    isLoading,
  };
};
